import { useState, useRef, useEffect, useCallback } from "react";
import { useDateFormat } from '@/hooks/useDateFormat';
import {
  MessageSquare,
  X,
  Send,
  Mic,
  MicOff,
  Brain,
  Stethoscope,
  FileText,
  TrendingUp,
  Pill,
  Calendar,
  AlertCircle,
  Sparkles,
  Copy,
  ThumbsUp,
  ThumbsDown,
  FileAudio,
  Loader2,
  StopCircle,
} from "lucide-react";
import { toast } from 'sonner';
import { AIContextManager } from "./AIContextManager";
import { AIVisitRecording } from "./AIVisitRecording";
import { useTranslation } from '../hooks/useTranslation';
import { useLLMStream } from '../hooks/useLLMStream';
import { useAIGateway } from '../hooks/useAIGateway';
import type { LLMMessage } from '../types/aiGateway';
import { useThemeStore } from '../stores/useThemeStore';
import { useAiAffordance } from '@/config/aiCapabilities';
import { Button } from '@/components/ui/button';
import { TextInput } from '@/components/ui/text-input';

interface ChatMessage {
  id: string;
  type: "user" | "assistant";
  content: string;
  timestamp: Date;
  feedback?: "positive" | "negative";
  isStreaming?: boolean;
}

interface AIContext {
  id: string;
  type: string;
  data: unknown;
  label: string;
}

interface AIAssistantPanelProps {
  isOpen: boolean;
  onClose: () => void;
  contexts: AIContext[];
  onRemoveContext: (id: string) => void;
  onClearAllContexts: () => void;
  onAddFullPageContext: () => void;
  isListening: boolean;
  onToggleListening: () => void;
}

// System prompt for the AI assistant
const getAssistantSystemPrompt = (aiProvider: string) => `You are ${aiProvider}, an advanced Medical AI Assistant integrated into an Electronic Medical Records (EMR) system. Your role is to:

1. Provide evidence-based clinical decision support
2. Help with differential diagnoses based on patient symptoms and history
3. Review medications for interactions and optimization
4. Generate clinical summaries and documentation
5. Assess patient risks and suggest follow-up plans
6. Answer medical questions with accurate, up-to-date information

Always be professional, accurate, and helpful. When providing medical advice, cite evidence when possible and acknowledge uncertainty when appropriate. Format your responses in a clear, readable way using markdown.

Remember: You are assisting healthcare professionals, not replacing their clinical judgment. Always recommend consulting with specialists when appropriate.`;

export const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({
  isOpen,
  onClose,
  contexts,
  onRemoveContext,
  onClearAllContexts,
  onAddFullPageContext,
  isListening,
  onToggleListening,
}) => {
  const { t } = useTranslation();
  const { formatTime } = useDateFormat();
  const { branding } = useThemeStore();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [activeTab, setActiveTab] = useState<'chat' | 'recording'>('chat');
  const [currentStreamingId, setCurrentStreamingId] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Ask-MAIE availability. The launcher already gates the entry, but the
  // panel is mounted globally — guard here too so an in-session flip
  // (quota tripped, stack down) disables the composer with a clear reason
  // instead of letting a send spinner→error.
  const assistant = useAiAffordance('assistant');

  // AI Gateway hooks
  const { isConnected, isConnecting, connect, error: gatewayError } = useAIGateway();
  const { isStreaming, streamedText, streamChat, cancelStream, clearStreamedText } = useLLMStream();

  // Auto-connect when panel opens
  useEffect(() => {
    if (isOpen && !isConnected && !isConnecting) {
      connect().catch((err) => {
        console.error('[AIAssistant] Failed to connect:', err);
      });
    }
  }, [isOpen, isConnected, isConnecting, connect]);

  // Scroll to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamedText]);

  // Update streaming message content
  useEffect(() => {
    if (currentStreamingId && streamedText) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentStreamingId
            ? { ...msg, content: streamedText }
            : msg
        )
      );
    }
  }, [streamedText, currentStreamingId]);

  const quickActions = [
    {
      icon: <Stethoscope className="w-4 h-4" />,
      label: t('ai.differentialDiagnosis'),
      prompt:
        "Based on the current context, what are the possible differential diagnoses?",
      color: "from-blue-500 to-blue-600",
    },
    {
      icon: <Pill className="w-4 h-4" />,
      label: t('ai.medicationReview'),
      prompt:
        "Review the patient's current medications for interactions and optimization opportunities",
      color: "from-purple-500 to-purple-600",
    },
    {
      icon: <FileText className="w-4 h-4" />,
      label: t('ai.clinicalSummary'),
      prompt:
        "Generate a comprehensive clinical summary based on the available information",
      color: "from-green-500 to-green-600",
    },
    {
      icon: <AlertCircle className="w-4 h-4" />,
      label: t('ai.riskAssessment'),
      prompt: "Identify potential health risks and red flags for this patient",
      color: "from-red-500 to-red-600",
    },
    {
      icon: <Calendar className="w-4 h-4" />,
      label: t('ai.followUpPlan'),
      prompt: "Suggest an appropriate follow-up plan and timeline",
      color: "from-orange-500 to-orange-600",
    },
    {
      icon: <TrendingUp className="w-4 h-4" />,
      label: t('ai.treatmentOptions'),
      prompt:
        "What are the evidence-based treatment options for this condition?",
      color: "from-indigo-500 to-indigo-600",
    },
  ];

  /**
   * Build context string from contexts for the AI
   */
  const buildContextString = useCallback((): string => {
    if (contexts.length === 0) return '';

    let contextStr = '\n\n## Current Context:\n';

    contexts.forEach((ctx) => {
      contextStr += `\n### ${ctx.label}\n`;
      if (typeof ctx.data === 'object') {
        contextStr += '```json\n' + JSON.stringify(ctx.data, null, 2) + '\n```\n';
      } else {
        contextStr += String(ctx.data) + '\n';
      }
    });

    return contextStr;
  }, [contexts]);

  /**
   * Build messages array for the LLM
   */
  const buildLLMMessages = useCallback((userMessage: string): LLMMessage[] => {
    const contextString = buildContextString();

    const llmMessages: LLMMessage[] = [
      {
        role: 'system',
        content: getAssistantSystemPrompt(branding.aiProvider || 'MAIE') + contextString,
      },
    ];

    // Add conversation history (last 10 messages)
    const recentMessages = messages.slice(-10);
    recentMessages.forEach((msg) => {
      llmMessages.push({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content,
      });
    });

    // Add the new user message
    llmMessages.push({
      role: 'user',
      content: userMessage,
    });

    return llmMessages;
  }, [messages, buildContextString, branding.aiProvider]);

  /**
   * Send a message and get streaming response
   */
  const handleSendMessage = useCallback(async (message: string) => {
    if (!message.trim()) return;
    if (!assistant.enabled) {
      toast.error(
        assistant.message ?? 'The AI assistant is unavailable right now.',
      );
      return;
    }

    // Add user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: "user",
      content: message,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    clearStreamedText();

    // Create placeholder for assistant message
    const assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      type: "assistant",
      content: "",
      timestamp: new Date(),
      isStreaming: true,
    };

    setMessages((prev) => [...prev, assistantMessage]);
    setCurrentStreamingId(assistantMessageId);

    try {
      // Ensure connected
      if (!isConnected) {
        await connect();
      }

      // Build messages and stream
      const llmMessages = buildLLMMessages(message);

      await streamChat(llmMessages, {
        onToken: (token) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? { ...msg, content: msg.content + token }
                : msg
            )
          );
        },
        onComplete: (fullText) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? { ...msg, content: fullText, isStreaming: false }
                : msg
            )
          );
          setCurrentStreamingId(null);
        },
        onError: (error) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? {
                    ...msg,
                    content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
                    isStreaming: false,
                  }
                : msg
            )
          );
          setCurrentStreamingId(null);
          toast.error('AI response failed: ' + error.message);
        },
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessageId
            ? {
                ...msg,
                content: `Sorry, I couldn't connect to the AI service: ${errorMessage}`,
                isStreaming: false,
              }
            : msg
        )
      );
      setCurrentStreamingId(null);
      toast.error('Failed to send message');
    }
  }, [isConnected, connect, buildLLMMessages, streamChat, clearStreamedText, assistant.enabled, assistant.message]);

  /**
   * Stop the current stream
   */
  const handleStopStream = useCallback(() => {
    cancelStream();
    if (currentStreamingId) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentStreamingId
            ? { ...msg, isStreaming: false }
            : msg
        )
      );
      setCurrentStreamingId(null);
    }
  }, [cancelStream, currentStreamingId]);

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
    toast.success('Message copied to clipboard');
  };

  const handleMessageFeedback = (
    messageId: string,
    feedback: "positive" | "negative"
  ) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === messageId ? { ...msg, feedback } : msg))
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 right-0 h-full w-[480px] shadow-2xl border-l flex flex-col z-50 animate-slide-in-right"
      style={{
        backgroundColor: 'var(--color-bg-primary)',
        borderColor: 'var(--color-border-primary)'
      }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold flex items-center gap-2">
                {t('ai.title')}
                <Sparkles className="w-4 h-4" />
              </h3>
              <p className="text-xs text-blue-100 flex items-center gap-1">
                {t('common.poweredByMAIE', { aiProvider: branding.aiProvider || 'MAIE AI' })}
                {isConnecting && <Loader2 className="w-3 h-3 animate-spin" />}
                {isConnected && <span className="w-2 h-2 bg-green-400 rounded-full" />}
                {!isConnected && !isConnecting && <span className="w-2 h-2 bg-red-400 rounded-full" />}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            className="text-white hover:bg-white/20 hover:text-white"
            aria-label="Close"
          >
            <X />
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b" style={{
        borderColor: 'var(--color-border-primary)',
        backgroundColor: 'var(--color-bg-primary)'
      }}>
        <button
          onClick={() => setActiveTab('chat')}
          className={`flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${activeTab === 'chat'
            ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
        >
          <MessageSquare size={16} />
          {t('ai.chat')}
        </button>
        <button
          onClick={() => setActiveTab('recording')}
          className={`flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${activeTab === 'recording'
            ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
        >
          <FileAudio size={16} />
          {t('ai.visitRecording')}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'chat' ? (
        <>
          {/* Context Manager — flex-shrink-0 so its growing content
              can't compress the chat-messages area and shove the
              input bar off-screen. The inner list already caps itself
              with max-h-64 + overflow-y-auto, so this wrapper just
              needs to refuse to grow past natural content height. */}
          <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <AIContextManager
              contexts={contexts}
              onRemove={onRemoveContext}
              onClearAll={onClearAllContexts}
              onAddFullPage={onAddFullPageContext}
            />
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto p-4" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
          <AIVisitRecording />
        </div>
      )}

      {activeTab === 'chat' && (
        <>
          {/* Quick Actions */}
          {messages.length === 0 && (
            <div className="p-4 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                {t('ai.quickActions')}
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => handleSendMessage(action.prompt)}
                    disabled={isStreaming || !assistant.enabled}
                    className="flex items-center gap-2 p-3 bg-white border border-gray-200 rounded-lg hover:shadow-md hover:border-gray-300 transition-all text-start group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div
                      className={`w-8 h-8 bg-gradient-to-r ${action.color} rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}
                    >
                      {action.icon}
                    </div>
                    <span className="text-xs font-medium text-gray-700">
                      {action.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('ai.readyToAssist')}
                </h3>
                <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">
                  {t('ai.assistDescription')}
                </p>
                {gatewayError && (
                  <p className="text-sm text-red-500 mt-2">
                    Connection error: {gatewayError.message}
                  </p>
                )}
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === "user" ? "justify-end" : "justify-start"
                  }`}
              >
                <div
                  className={`max-w-[85%] ${message.type === "user"
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                    : "bg-white border border-gray-200"
                    } rounded-2xl p-4 shadow-sm`}
                >
                  <div
                    className={`text-sm leading-relaxed whitespace-pre-wrap ${message.type === "user" ? "text-white" : "text-gray-800"
                      }`}
                  >
                    {message.content || (message.isStreaming && <Loader2 className="w-4 h-4 animate-spin" />)}
                  </div>

                  {message.type === "assistant" && !message.isStreaming && message.content && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => handleCopyMessage(message.content)}
                      >
                        <Copy />
                        {t('common.copy')}
                      </Button>
                      <div className="flex items-center gap-1 ms-auto">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() =>
                            handleMessageFeedback(message.id, "positive")
                          }
                          className={message.feedback === "positive" ? "text-green-600" : ""}
                        >
                          <ThumbsUp />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() =>
                            handleMessageFeedback(message.id, "negative")
                          }
                          className={message.feedback === "negative" ? "text-red-600" : ""}
                        >
                          <ThumbsDown />
                        </Button>
                      </div>
                    </div>
                  )}

                  <p
                    className={`text-xs mt-2 ${message.type === "user" ? "text-blue-100" : "text-gray-500"
                      }`}
                  >
                    {formatTime(message.timestamp, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {message.isStreaming && " • Typing..."}
                  </p>
                </div>
              </div>
            ))}

            <div ref={chatEndRef} />
          </div>

          {/* Input Area — flex-shrink-0 belt-and-suspenders so the
              input never gets squeezed out by neighbours that grow
              (already the case for messages because of flex-1, but
              explicit here too). */}
          <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
            {!assistant.enabled && (
              <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 text-amber-800 text-xs">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  {assistant.message ??
                    'The AI assistant is turned off for this clinic. You can keep working manually.'}
                </span>
              </div>
            )}
            <div className="flex gap-3 mb-3">
              <TextInput
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && !isStreaming && handleSendMessage(inputMessage)
                }
                placeholder={t('ai.placeholder')}
                disabled={isStreaming || !assistant.enabled}
                className="flex-1"
              />
              {isStreaming ? (
                <Button
                  variant="destructive"
                  size="icon-lg"
                  onClick={handleStopStream}
                  title="Stop generating"
                  aria-label="Stop generating"
                >
                  <StopCircle />
                </Button>
              ) : (
                <Button
                  size="icon-lg"
                  onClick={() => handleSendMessage(inputMessage)}
                  disabled={!inputMessage.trim() || !isConnected || !assistant.enabled}
                  aria-label="Send"
                >
                  <Send />
                </Button>
              )}
            </div>

            <button
              onClick={onToggleListening}
              disabled={isStreaming}
              className={`w-full px-4 py-3 rounded-xl transition-all flex items-center justify-center font-medium disabled:opacity-50 ${isListening
                ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg animate-pulse"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              {isListening ? (
                <MicOff className="w-4 h-4 me-2" />
              ) : (
                <Mic className="w-4 h-4 me-2" />
              )}
              {isListening ? t('ai.stopListening') : t('ai.voiceInput')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
